import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import PhoneMockup from '../PhoneMockup';
import useIsMobile from '../../hooks/useIsMobile';
import { useLandingLanguage } from '../../context/LandingLanguageContext';
import HeroWalletPhoneScreen from './HeroWalletPhoneScreen';

/** `public/assets/media/videos/background-final.mp4` — hero loop. */
const HERO_VIDEO_SRC = `${process.env.PUBLIC_URL || ''}/assets/media/videos/background-final.mp4`;

/* Hero background — video with cinematic grade layers on top */
const VideoBg = () => (
  <>
    <style>{`
      @keyframes pulse-ring-hero {
        0%   { transform: scale(0.92); opacity: 0.5; }
        100% { transform: scale(1.32); opacity: 0; }
      }
      @keyframes gold-shimmer {
        0%   { background-position: 0% center; }
        100% { background-position: 200% center; }
      }
    `}</style>

    {/* Actual Morocco video */}
    <video
      autoPlay
      muted
      loop
      playsInline
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
    >
      <source src={HERO_VIDEO_SRC} type="video/mp4" />
    </video>

    {/* Light wash — keep video colors visible */}
    <div style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', background: 'rgba(4,2,0,0.32)' }} />

    {/* Subtle warmth (was heavy amber/gold) */}
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 2,
        pointerEvents: 'none',
        background: `
          radial-gradient(ellipse 78% 60% at 50% 36%, rgba(212,175,55,0.08) 0%, rgba(110,48,10,0.07) 32%, transparent 68%),
          radial-gradient(ellipse 52% 40% at 50% 16%, rgba(245,210,130,0.06) 0%, transparent 52%)
        `,
      }}
    />

    {/* Softer vignette so mid-frame video reads true */}
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 3,
        pointerEvents: 'none',
        background:
          'radial-gradient(ellipse 118% 108% at 50% 50%, transparent 40%, rgba(52,22,8,0.28) 78%, rgba(10,4,0,0.52) 100%)',
      }}
    />
  </>
);

/* ═══════════════════════════════════════════════════════
   ZELLIGE PATTERN
═══════════════════════════════════════════════════════ */
const ZelligeOverlay = () => (
  <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.04, pointerEvents: 'none', zIndex: 4 }} aria-hidden>
    <defs>
      <pattern id="z2" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
        <rect x="12" y="12" width="56" height="56" fill="none" stroke="#D4AF37" strokeWidth="0.9" />
        <rect x="12" y="12" width="56" height="56" fill="none" stroke="#D4AF37" strokeWidth="0.9" transform="rotate(45 40 40)" />
        <rect x="26" y="26" width="28" height="28" fill="none" stroke="#D4AF37" strokeWidth="0.6" transform="rotate(45 40 40)" />
        <circle cx="40" cy="40" r="3" fill="#D4AF37" opacity="0.5" />
        <circle cx="0" cy="0" r="5" fill="none" stroke="#D4AF37" strokeWidth="0.6" />
        <circle cx="80" cy="0" r="5" fill="none" stroke="#D4AF37" strokeWidth="0.6" />
        <circle cx="0" cy="80" r="5" fill="none" stroke="#D4AF37" strokeWidth="0.6" />
        <circle cx="80" cy="80" r="5" fill="none" stroke="#D4AF37" strokeWidth="0.6" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#z2)" />
  </svg>
);

/* ═══════════════════════════════════════════════════════
   GOLD DUST PARTICLES
═══════════════════════════════════════════════════════ */
const GoldParticles = () => {
  const particles = useMemo(() => Array.from({ length: 18 }, (_, i) => ({
    id: i, left: `${(i * 5.3 + 2) % 96}%`,
    top: `${(i * 7.7 + 5) % 92}%`,
    size: (i % 3) + 1.5, duration: 9 + (i % 7),
    delay: (i * 0.55) % 7, opacity: 0.35 + (i % 4) * 0.15,
  })), []);
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 5 }}>
      {particles.map((p) => (
        <motion.div key={p.id}
          style={{ position: 'absolute', left: p.left, top: p.top, width: p.size, height: p.size, borderRadius: '50%', background: 'radial-gradient(circle, #E8CA6A 0%, #D4AF37 100%)', boxShadow: `0 0 ${p.size * 2}px rgba(212,175,55,0.5)` }}
          animate={{ y: [0, -100, -200], opacity: [0, p.opacity, 0] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: 'easeOut' }}
        />
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MEDINA SKYLINE SILHOUETTE
═══════════════════════════════════════════════════════ */
const MedinaSilhouette = () => (
  <svg viewBox="0 0 1440 220" preserveAspectRatio="none"
    style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '220px', pointerEvents: 'none', zIndex: 6 }} aria-hidden>
    <defs>
      <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.22" />
        <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.04" />
      </linearGradient>
    </defs>
    <rect x="0" y="200" width="80" height="20" fill="url(#sg)" />
    <rect x="80" y="185" width="60" height="35" fill="url(#sg)" />
    <rect x="140" y="170" width="70" height="50" fill="url(#sg)" />
    <rect x="210" y="155" width="55" height="65" fill="url(#sg)" />
    <rect x="265" y="162" width="65" height="58" fill="url(#sg)" />
    <rect x="330" y="145" width="75" height="75" fill="url(#sg)" />
    <rect x="405" y="130" width="55" height="90" fill="url(#sg)" />
    <path d="M405 130 Q432 100 460 130" fill="url(#sg)" />
    <rect x="460" y="138" width="65" height="82" fill="url(#sg)" />
    <rect x="525" y="150" width="55" height="70" fill="url(#sg)" />
    <rect x="580" y="132" width="50" height="88" fill="url(#sg)" />
    {/* Central minaret */}
    <rect x="638" y="28" width="70" height="192" fill="url(#sg)" />
    <rect x="645" y="20" width="56" height="14" fill="url(#sg)" />
    <rect x="650" y="8" width="46" height="16" fill="url(#sg)" />
    <ellipse cx="673" cy="8" rx="23" ry="12" fill="url(#sg)" />
    <rect x="671" y="0" width="6" height="10" fill="#D4AF37" opacity="0.25" />
    {[58, 88, 118, 148].map(y => (
      <path key={y} d={`M645 ${y} Q673 ${y - 12} 701 ${y}`} fill="none" stroke="#D4AF37" strokeWidth="1" opacity="0.28" />
    ))}
    {/* Right cluster */}
    <rect x="710" y="130" width="55" height="90" fill="url(#sg)" />
    <path d="M755 130 Q782 100 810 130" fill="url(#sg)" />
    <rect x="765" y="138" width="65" height="82" fill="url(#sg)" />
    <rect x="830" y="150" width="60" height="70" fill="url(#sg)" />
    <rect x="890" y="145" width="55" height="75" fill="url(#sg)" />
    <rect x="945" y="158" width="65" height="62" fill="url(#sg)" />
    <rect x="1010" y="168" width="75" height="52" fill="url(#sg)" />
    <rect x="1085" y="178" width="60" height="42" fill="url(#sg)" />
    <rect x="1145" y="172" width="70" height="48" fill="url(#sg)" />
    <rect x="1215" y="182" width="65" height="38" fill="url(#sg)" />
    <rect x="1280" y="188" width="75" height="32" fill="url(#sg)" />
    <rect x="1355" y="198" width="85" height="22" fill="url(#sg)" />
    <rect x="1050" y="108" width="45" height="112" fill="url(#sg)" />
    <ellipse cx="1072" cy="108" rx="16" ry="10" fill="url(#sg)" />
  </svg>
);

/* ═══════════════════════════════════════════════════════
   MAIN HERO
═══════════════════════════════════════════════════════ */
const MoroccoVideoHero = () => {
  const isMobile = useIsMobile();
  const { t } = useLandingLanguage();

  return (
    <section
      id="hero"
      style={{
        position: 'relative',
        height: isMobile ? 'auto' : '100vh',
        minHeight: isMobile ? '100vh' : '700px',
        overflow: 'hidden',
        background: '#0F0F0F',
      }}
    >
      {/* Background layers (no video — see VideoBg) */}
      <VideoBg />
      <ZelligeOverlay />
      <GoldParticles />

      {/* Bottom: blend into page below */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '38%', background: 'linear-gradient(to top, #0F0F0F 0%, rgba(15,15,15,0.35) 55%, transparent 100%)', zIndex: 7 }} />
      {/* Top: smooth behind navbar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '18%', background: 'linear-gradient(to bottom, rgba(4,2,0,0.38) 0%, transparent 100%)', zIndex: 7 }} />
      {/* Left text scrim: desktop only */}
      {!isMobile && (
        <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '52%', background: 'linear-gradient(to right, rgba(4,2,0,0.42) 0%, rgba(4,2,0,0.22) 60%, transparent 100%)', zIndex: 7 }} />
      )}

      {/* ── Content ── */}
      <div style={{
        position: isMobile ? 'relative' : 'absolute',
        inset: isMobile ? undefined : 0,
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        padding: isMobile ? '0 24px' : '0 5vw',
        zIndex: 10,
      }}>
        <div style={{
          maxWidth: '1280px',
          width: '100%',
          margin: '0 auto',
          display: isMobile ? 'flex' : 'grid',
          flexDirection: isMobile ? 'column' : undefined,
          gridTemplateColumns: isMobile ? undefined : 'minmax(0,1.15fr) minmax(0,0.85fr)',
          gap: isMobile ? '24px' : '60px',
          alignItems: 'center',
          /* Extra top space on mobile so the hero badge clears the fixed navbar (esp. two-row + language toggle). */
          paddingTop: isMobile ? '132px' : '80px',
          paddingBottom: isMobile ? '60px' : '0',
        }}>

          {/* LEFT — Text (min-heights keep EN/FR swap from shifting the phone column) */}
          <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: -16, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(6,5,2,0.78)',
                border: '1px solid rgba(212,175,55,0.28)',
                borderRadius: '100px',
                padding: '6px 18px 6px 12px',
                marginBottom: '32px',
                backdropFilter: 'blur(12px)',
                minHeight: '34px',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#D4AF37', boxShadow: '0 0 6px rgba(212,175,55,0.8)', animation: 'pulse-ring-hero 1.8s ease-out infinite' }} />
              <span style={{ color: '#D4AF37', fontSize: '11px', fontWeight: '700', letterSpacing: '1.2px', textTransform: 'uppercase' }}>{t('hero.badge')}</span>
            </motion.div>

            <div style={{ marginBottom: '28px', minHeight: 'calc(clamp(44px, 5.8vw, 82px) * 2.12)' }}>
              {/* Headline — line 1 */}
              <motion.div
                style={{ marginBottom: '4px' }}
                initial={{ opacity: 0, y: 36 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.32 }}
              >
                <h1 style={{ fontSize: 'clamp(44px, 5.8vw, 82px)', fontWeight: '900', lineHeight: 1.0, letterSpacing: '-3px', margin: 0, color: '#F5F1EA', fontFamily: 'Inter, sans-serif', textShadow: '0 4px 32px rgba(0,0,0,0.7)' }}>
                  {t('hero.line1')}
                </h1>
              </motion.div>

              {/* Headline — line 2 (gold) */}
              <motion.div
                initial={{ opacity: 0, y: 36 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.46 }}
              >
                <h1 style={{ fontSize: 'clamp(44px, 5.8vw, 82px)', fontWeight: '900', lineHeight: 1.05, letterSpacing: '-3px', margin: 0, fontFamily: 'Inter, sans-serif' }}>
                  <span style={{
                    background: 'linear-gradient(135deg, #D4AF37 0%, #F0D070 45%, #D4AF37 100%)',
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    animation: 'gold-shimmer 4s linear infinite',
                  }}>
                    {t('hero.line2')}
                  </span>
                </h1>
              </motion.div>
            </div>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.62 }}
              style={{
                color: 'rgba(245,241,234,0.72)',
                fontSize: 'clamp(15px, 1.3vw, 18px)',
                lineHeight: 1.65,
                marginBottom: '40px',
                maxWidth: isMobile ? '100%' : '440px',
                marginLeft: isMobile ? 'auto' : undefined,
                marginRight: isMobile ? 'auto' : undefined,
                fontWeight: '400',
                textShadow: '0 2px 10px rgba(0,0,0,0.5)',
                minHeight: 'calc(1.65em * 2)',
                boxSizing: 'border-box',
              }}
            >
              {t('hero.sub1')}<br />{t('hero.sub2')}
            </motion.p>

          </div>

          {/* RIGHT — Phone */}
          <motion.div
            initial={{ opacity: 0, x: isMobile ? 0 : 60, scale: 0.88, rotateY: isMobile ? 0 : 8 }}
            animate={{ opacity: 1, x: 0, scale: 1, rotateY: 0 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', height: isMobile ? '620px' : '560px', perspective: '1000px' }}
          >
            {/* Ambient glow */}
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.6, ease: 'easeOut', delay: 0.8 }}
              style={{ position: 'absolute', width: isMobile ? '320px' : '420px', height: isMobile ? '320px' : '420px', background: 'radial-gradient(circle, rgba(212,175,55,0.14) 0%, transparent 70%)', filter: 'blur(60px)', zIndex: 0 }}
            />

            {/* Phone — floats after entrance */}
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut', delay: 1.6 }}
              style={{ position: 'relative', zIndex: 10 }}
            >
              <PhoneMockup size="lg" glow>
                <HeroWalletPhoneScreen />
              </PhoneMockup>
            </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default MoroccoVideoHero;
